import { ALL_FEATURES, type Features, type SessionUser } from "@corklog/shared";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

import { fetchCurrentUser, logOut as apiLogOut } from "./api.js";
import { forgetCellars, useCellar } from "./cellar.js";
import { forgetTheCellar } from "./offline.js";

type SessionState =
  | { status: "unknown" }
  | { status: "signedIn"; user: SessionUser }
  | { status: "signedOut" };

type Session = SessionState & {
  signedIn: (user: SessionUser) => void;
  signOut: () => Promise<void>;
  /** Ask again who is signed in and what they may ask the model — after the
   *  administrator has thrown one of their own switches, so their own page
   *  follows at once rather than on the next reload. */
  refresh: () => Promise<void>;
};

const SessionContext = createContext<Session | null>(null);

/**
 * Who is logged in, asked once at startup. Until the answer arrives the state
 * is `unknown` rather than `signedOut` — the difference matters, because
 * treating "not yet asked" as "not logged in" flashes the sign-in screen at
 * somebody who is already signed in on every reload.
 */
export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SessionState>({ status: "unknown" });

  useEffect(() => {
    let cancelled = false;
    void fetchCurrentUser()
      .then((user) => {
        if (cancelled) return;
        setState(user === null ? { status: "signedOut" } : { status: "signedIn", user });
      })
      .catch(() => {
        if (!cancelled) setState({ status: "signedOut" });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const signedIn = useCallback((user: SessionUser) => {
    setState({ status: "signedIn", user });
  }, []);

  const signOut = useCallback(async () => {
    await apiLogOut();
    // The cookie is gone the moment the API answers; the copy of the cellar on
    // the phone's disk is not, and it would outlive the session it was read
    // under. Told after the logout succeeded rather than before, so a failed
    // sign-out does not blind a cellar that is still signed in.
    forgetTheCellar();
    // And the roles held in memory, for the same reason one level up: the next
    // person to sign in on this browser may read these cellars differently, or
    // not at all.
    forgetCellars();
    setState({ status: "signedOut" });
  }, []);

  // A failure leaves the session as it was: the switches were saved either
  // way, and the page will be right on the next reload.
  const refresh = useCallback(async () => {
    const user = await fetchCurrentUser().catch(() => undefined);
    if (user === undefined) return;
    setState(user === null ? { status: "signedOut" } : { status: "signedIn", user });
  }, []);

  return (
    <SessionContext.Provider value={{ ...state, signedIn, signOut, refresh }}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSession(): Session {
  const session = useContext(SessionContext);
  if (session === null) throw new Error("useSession outside SessionProvider.");
  return session;
}

/**
 * Which of the three questions to the model this account may ask — the
 * administrator's switches, resolved by the server. Every one on until the
 * session is known, which is the same as before the switches existed; a page
 * that reaches a question before then is one whose route will still refuse it.
 *
 * Read from the context directly rather than through `useSession`, which
 * throws outside a provider: the components that ask are drawn on their own
 * by their tests, and a question that is on is what those tests are about.
 *
 * **In a cellar, that cellar's answer where the administrator gave it one**,
 * and the account's everywhere else. The cellar is the one the provider above
 * the routes is holding, which is null for a moment on a link opened cold —
 * the account's answer stands in for that moment, and the route decides.
 */
/**
 * Whether this account is shown *Upptäck* — the server's answer on the
 * session. False until the session is known, unlike `useFeatures`: what hangs
 * on it is a link, and a link that appears a moment late is better than one
 * that appears and then goes. Read from the context directly, for the reason
 * `useFeatures` gives.
 */
export function useMayDiscover(): boolean {
  const session = useContext(SessionContext);
  return session?.status === "signedIn" && session.user.mayDiscover;
}

/**
 * The signed-in account, or null — for the pieces drawn on every page that
 * only need to know who is reading, such as a cellar's *Mer*. Read from the
 * context directly, as `useFeatures` is, so a screen rendered on its own in a
 * test does not need a session around it to draw its heading.
 */
export function useSignedInUser(): SessionUser | null {
  const session = useContext(SessionContext);
  return session?.status === "signedIn" ? session.user : null;
}

/**
 * Ask the server for the session again, or do nothing without one — for the
 * screens that change something the session carries and are drawn on their
 * own in tests. Read from the context directly, as `useSignedInUser` is.
 */
export function useRefreshSession(): () => Promise<void> {
  const session = useContext(SessionContext);
  return session?.refresh ?? (() => Promise.resolve());
}

export function useFeatures(): Features {
  const session = useContext(SessionContext);
  const cellarId = useCellar()?.id;
  if (session?.status !== "signedIn") return ALL_FEATURES;
  const inCellar =
    cellarId === undefined ? undefined : session.user.cellarFeatures[cellarId];
  return inCellar ?? session.user.features;
}
